import { z } from 'zod';

/** Stable machine-readable error codes returned alongside RFC 9457 problem details. */
export const ERROR_CODES = [
  'validation_failed',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'version_mismatch',
  'precondition_failed',
  'rate_limited',
  'payload_too_large',
  'internal',
] as const;
export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const FieldErrorSchema = z.object({
  path: z.string(),
  message: z.string(),
});
export type FieldError = z.infer<typeof FieldErrorSchema>;

/** RFC 9457 problem+json body. `type` is a stable URI per error code. */
export const ProblemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.int().min(400).max(599),
  code: ErrorCodeSchema,
  detail: z.string().optional(),
  instance: z.string().optional(),
  errors: z.array(FieldErrorSchema).optional(),
});
export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

export const PROBLEM_TYPE_BASE = 'https://servicetim.dev/problems/';

const DEFAULT_STATUS: Record<ErrorCode, number> = {
  validation_failed: 400,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  version_mismatch: 409,
  precondition_failed: 412,
  rate_limited: 429,
  payload_too_large: 413,
  internal: 500,
};

const DEFAULT_TITLE: Record<ErrorCode, string> = {
  validation_failed: 'Validation failed',
  unauthorized: 'Authentication required',
  forbidden: 'Forbidden',
  not_found: 'Not found',
  conflict: 'Conflict',
  version_mismatch: 'Version mismatch',
  precondition_failed: 'Precondition failed',
  rate_limited: 'Too many requests',
  payload_too_large: 'Payload too large',
  internal: 'Internal error',
};

export interface ProblemOptions {
  detail?: string;
  instance?: string;
  errors?: FieldError[];
  status?: number;
}

export function problem(code: ErrorCode, options: ProblemOptions = {}): ProblemDetails {
  return {
    type: `${PROBLEM_TYPE_BASE}${code}`,
    title: DEFAULT_TITLE[code],
    status: options.status ?? DEFAULT_STATUS[code],
    code,
    ...(options.detail !== undefined ? { detail: options.detail } : {}),
    ...(options.instance !== undefined ? { instance: options.instance } : {}),
    ...(options.errors !== undefined ? { errors: options.errors } : {}),
  };
}

/** Converts Zod issues into flat field errors (`a.b[0].c`). */
export function fieldErrorsFromZod(error: z.ZodError): FieldError[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    message: issue.message,
  }));
}

/** Thrown inside handlers/domain code; the HTTP layer turns it into problem+json. */
export class AppError extends Error {
  readonly problem: ProblemDetails;

  constructor(code: ErrorCode, options: ProblemOptions = {}) {
    const p = problem(code, options);
    super(p.detail ?? p.title);
    this.name = 'AppError';
    this.problem = p;
  }
}
